import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Folder, FolderKanban, ListTodo, AlertTriangle, Users } from 'lucide-react'
import { apiClient } from '../services/api'

interface SearchResult {
  id: number
  type: 'project' | 'program' | 'task' | 'raid' | 'user'
  title: string
  subtitle?: string
  path: string
}

const TYPE_META: Record<SearchResult['type'], { icon: React.ReactNode; label: string; color: string }> = {
  project: { icon: <Folder className="w-4 h-4" aria-hidden="true" />, label: 'Project', color: 'text-blue-600' },
  program: { icon: <FolderKanban className="w-4 h-4" aria-hidden="true" />, label: 'Program', color: 'text-purple-600' },
  task: { icon: <ListTodo className="w-4 h-4" aria-hidden="true" />, label: 'Task', color: 'text-green-600' },
  raid: { icon: <AlertTriangle className="w-4 h-4" aria-hidden="true" />, label: 'RAID', color: 'text-orange-600' },
  user: { icon: <Users className="w-4 h-4" aria-hidden="true" />, label: 'Person', color: 'text-gray-600' },
}

async function searchAll(query: string): Promise<SearchResult[]> {
  if (query.length < 2) return []
  try {
    const q = encodeURIComponent(query)
    const [projects, programs, tasks] = await Promise.allSettled([
      apiClient.get(`/api/v1/projects?search=${q}&limit=5`),
      apiClient.get(`/api/v1/programs?search=${q}&limit=5`),
      apiClient.get(`/api/v1/tasks?search=${q}&limit=5`),
    ])

    const results: SearchResult[] = []

    if (projects.status === 'fulfilled') {
      const items = Array.isArray(projects.value.data) ? projects.value.data : projects.value.data?.items ?? []
      items.forEach((p: SearchHit) => results.push({
        id: p.id, type: 'project', title: p.name,
        subtitle: p.status, path: `/project/${p.id}`,
      }))
    }
    if (programs.status === 'fulfilled') {
      const items = Array.isArray(programs.value.data) ? programs.value.data : programs.value.data?.items ?? []
      items.forEach((p: SearchHit) => results.push({
        id: p.id, type: 'program', title: p.name,
        subtitle: p.status, path: `/program/${p.id}`,
      }))
    }
    if (tasks.status === 'fulfilled') {
      const items = Array.isArray(tasks.value.data) ? tasks.value.data : tasks.value.data?.items ?? []
      items.forEach((t: SearchHit) => results.push({
        id: t.id, type: 'task', title: t.title || t.name,
        subtitle: t.status, path: `/task/${t.id}`,
      }))
    }

    return results
  } catch {
    return []
  }
}

type SearchHit = { id: number; name: string; title: string; status?: string }

export function GlobalSearch() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(v => !v)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery('')
      setResults([])
      setSelected(0)
    }
  }, [open])

  // Debounced search
  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    const t = setTimeout(async () => {
      setLoading(true)
      const r = await searchAll(query)
      setResults(r)
      setSelected(0)
      setLoading(false)
    }, 250)
    return () => clearTimeout(t)
  }, [query])

  const handleSelect = useCallback((result: SearchResult) => {
    setOpen(false)
    navigate(result.path)
  }, [navigate])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelected(s => Math.min(s + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelected(s => Math.max(s - 1, 0))
    } else if (e.key === 'Enter' && results[selected]) {
      handleSelect(results[selected])
    }
  }

  // Group by type
  const groups: Partial<Record<SearchResult['type'], SearchResult[]>> = {}
  results.forEach(r => {
    if (!groups[r.type]) groups[r.type] = []
    groups[r.type]!.push(r)
  })

  if (!open) return (
    <button
      onClick={() => setOpen(true)}
      className="flex items-center gap-2 px-3 py-1.5 bg-[#2a3a4d] border border-[#4a5e72] rounded-lg text-[#9aabb9] hover:border-[#6a8ea8] hover:text-white transition-colors text-sm min-h-[36px]"
      aria-label="Open search (Cmd+K)"
    >
      <Search className="w-4 h-4" aria-hidden="true" />
      <span className="hidden sm:inline">Search…</span>
      <kbd className="hidden sm:inline text-[10px] bg-[#1a2942] border border-[#4a5e72] px-1.5 py-0.5 rounded font-mono" aria-hidden="true">
        ⌘K
      </kbd>
    </button>
  )

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-start justify-center pt-16 px-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={e => { if (e.target === overlayRef.current) setOpen(false) }}
      role="dialog"
      aria-label="Search"
      aria-modal="true"
    >
      <div className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-expanded={results.length > 0}
            aria-autocomplete="list"
            aria-controls="search-results"
            aria-activedescendant={results[selected] ? `search-result-${results[selected].id}` : undefined}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search tasks, projects, programs, RAID items…"
            className="flex-1 text-sm text-gray-900 placeholder-gray-400 outline-none bg-transparent"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 hover:bg-gray-100 rounded transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
              aria-label="Clear search"
            >
              <X className="w-4 h-4 text-gray-400" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Results */}
        <div id="search-results" role="listbox" aria-label="Search results">
          {loading && (
            <p className="text-center py-4 text-sm text-gray-500" aria-live="polite">Searching…</p>
          )}
          {!loading && query.length >= 2 && results.length === 0 && (
            <p className="text-center py-6 text-sm text-gray-500" aria-live="polite">
              No results for "<strong>{query}</strong>"
            </p>
          )}
          {!loading && results.length > 0 && (
            <ul className="max-h-80 overflow-y-auto divide-y divide-gray-50 pb-1">
              {(Object.entries(groups) as [SearchResult['type'], SearchResult[]][]).map(([type, items]) => {
                const meta = TYPE_META[type]
                return (
                  <li key={type}>
                    <p className="px-4 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">
                      {meta.label}s
                    </p>
                    {items.map((result) => {
                      const globalIdx = results.indexOf(result)
                      return (
                        <button
                          key={result.id}
                          id={`search-result-${result.id}`}
                          role="option"
                          aria-selected={globalIdx === selected}
                          onClick={() => handleSelect(result)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                            globalIdx === selected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <span className={`flex-shrink-0 ${meta.color}`}>{meta.icon}</span>
                          <span className="flex-1 min-w-0">
                            <span className="text-sm text-gray-900 block truncate">{result.title}</span>
                            {result.subtitle && (
                              <span className="text-xs text-gray-500 capitalize">{result.subtitle}</span>
                            )}
                          </span>
                        </button>
                      )
                    })}
                  </li>
                )
              })}
            </ul>
          )}
          {!query && (
            <div className="px-4 py-4 text-xs text-gray-400 flex items-center gap-2">
              <kbd className="bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono" aria-hidden="true">↑↓</kbd> navigate
              <kbd className="bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono ml-2" aria-hidden="true">↵</kbd> select
              <kbd className="bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono ml-2" aria-hidden="true">Esc</kbd> close
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
